import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Flyout, {
  FlyoutHeader,
  FlyoutPanelContent,
} from 'components/common/omnia/flyout-component';
import FormulaPanelHeader from './_formula-panel-header-component';
import FormulaPanelContentContainer from 'containers/feature/formula-panel/formula-panel-content-container';
import FormulaCancelModal from './formula-cancel-modal-component';
import FormulaForm from 'models/form/formula-form-model';
import { isNullOrUndefined } from 'utils/object-utils';
import { withRouter } from 'utils/with-router-hoc';
import { ROUTE_CONSTANTS } from 'constants/util/route-constants';
export const FORMULA_PANEL_BLOCK = 'formula-panel';
export const FORMULA_PANEL_ID_BLOCK = 'formula-panel-id';
export const GLOBAL_HEADER_BACK_BUTTON_ID = 'global-header-id__back-button';

class FormulaPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showNavigationConfirm: false,
      pendingNavigation: null,
    };
    this.isBlockingNavigation = false;
    this.shouldBlockNavigation = false; // Start disabled, enable only when form is modified
    this._hasSetupHistory = false;
  }

  componentDidMount() {
    this.shouldBlockNavigation = true;
    this._setupNavigationBlocking();
  }

  componentWillUnmount() {
    this._cleanupNavigationBlocking();
    this._cleanupGlobalNavigationInterception();
  }
  componentDidUpdate(prevProps) {
    const { formulaInProgress } = this.props;
    // Only update navigation blocking if the modified state has changed
    if (
      prevProps.formulaInProgress.hasBeenModified !==
      formulaInProgress.hasBeenModified
    ) {
      if (formulaInProgress.hasBeenModified) {
        this.shouldBlockNavigation = true;
        this._setupGlobalNavigationInterception();
        if (!this._hasSetupHistory) {
          this._setupNavigationBlocking();
        }
      } else {
        this.shouldBlockNavigation = false;
      }
    }
  }
  /** Prevent browser from navigating away from statenement content page
   * if the they type a random url and if formula is not saved */
  _setupNavigationBlocking = () => {
    window.addEventListener('beforeunload', this._blockUrlNavigation);
    window.addEventListener('popstate', this._handlePopState);

    // Setup initial history state
    window.history.pushState(null, '', window.location.pathname);
    this._hasSetupHistory = true;
  };

  _cleanupNavigationBlocking = () => {
    window.removeEventListener('beforeunload', this._blockUrlNavigation);
    window.removeEventListener('popstate', this._handlePopState);
  };

  /** Set up global navigation interception using DOM event delegation */
  _setupGlobalNavigationInterception = () => {
    document.addEventListener('click', this._handleGlobalClick, true);
  };

  _cleanupGlobalNavigationInterception = () => {
    document.removeEventListener('click', this._handleGlobalClick, true);
  };

  /** Handle global clicks to intercept navigation attempts */
  _handleGlobalClick = (event) => {
    if (
      !this.shouldBlockNavigation ||
      !this.props.formulaInProgress?.hasBeenModified
    ) {
      return;
    }
    const target = event.target.closest('button');
    if (target && target.id === GLOBAL_HEADER_BACK_BUTTON_ID) {
      event.preventDefault();
      event.stopPropagation();
      this._triggerNavigationConfirm(ROUTE_CONSTANTS.HOME_PAGE);
    }
  };

  _blockUrlNavigation = (e) => {
    if (this.shouldBlockNavigation) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  _handlePopState = (event) => {
    if (this.isBlockingNavigation || !this.shouldBlockNavigation) {
      return;
    }

    this.isBlockingNavigation = true;

    setTimeout(() => {
      window.history.pushState(null, '', window.location.pathname);
      this.setState({
        showNavigationConfirm: true,
        pendingNavigation: 'back',
      });

      setTimeout(() => {
        this.isBlockingNavigation = false;
      }, 50);
    }, 0);
  };

  _triggerNavigationConfirm = (destination) => {
    this.setState({
      showNavigationConfirm: true,
      pendingNavigation: destination,
    });
  };

  _confirmNavigation = () => {
    const { pendingNavigation } = this.state;
    this.setState({
      showNavigationConfirm: false,
      pendingNavigation: null,
    });
    return pendingNavigation;
  };

  _cancelNavigation = () => {
    this.setState({
      showNavigationConfirm: false,
      pendingNavigation: null,
    });
  };

  _toggleCancelModal = () => {
    const {
      toggleFormulaCancelModal,
      cancelModal,
      openCancelModalAfterModalConfirm,
      storeSelectedNoteId,
      storeSelectedNotePseudoId,
    } = this.props;
    const { openFormulaCancelModal } = cancelModal;
    toggleFormulaCancelModal(!openFormulaCancelModal);
    if (openFormulaCancelModal) {
      openCancelModalAfterModalConfirm(false);
      storeSelectedNoteId(null);
      storeSelectedNotePseudoId(null);
    }
  };

  /** First action fired when cancel or back button is clicked, determines if
   * we should show cancel modal or just close the formula panel
   */
  _determineActionOnCancel = () => {
    this._triggerNavigationConfirm();
  };

  /** Action fired from the cancel modal when we have confirmed that we'd like to
   * discard our changes and close the formula panel
   */
  _confirmCancelFromCancelModal = () => {
    const {
      closeFormulaPanelFlyout,
      toggleNotesPanel,
      selectNoteFromContentPanel,
      cancelModal,
      onSectionClick,
      cancelCopyFormula,
      navigate,
    } = this.props;
    const {
      openFormulaCancelModal,
      selectedNoteId,
      selectedPseudoNoteId,
      openNotePanelAfterConfirm,
      openSectionPanelAfterConfirm,
      selectedSectionId,
    } = cancelModal;
    let fetchElementsAfterModalClose = true;
    const pendingNavigation = this._confirmNavigation();

    if (openFormulaCancelModal) {
      this._toggleCancelModal();
    }

    cancelCopyFormula();

    if (pendingNavigation) {
      navigate(ROUTE_CONSTANTS.HOME_PAGE);
      return;
    }

    if (openFormulaCancelModal && openNotePanelAfterConfirm) {
      closeFormulaPanelFlyout(fetchElementsAfterModalClose);
      if (selectedNoteId === null || selectedPseudoNoteId === null) {
        toggleNotesPanel();
      } else {
        selectNoteFromContentPanel({
          elementId: selectedNoteId,
          pseudoElementId: selectedPseudoNoteId,
        });
      }
    } else if (
      openSectionPanelAfterConfirm &&
      openFormulaCancelModal &&
      !isNullOrUndefined(selectedSectionId)
    ) {
      closeFormulaPanelFlyout(fetchElementsAfterModalClose);
      onSectionClick({ sectionId: selectedSectionId });
    } else {
      closeFormulaPanelFlyout(fetchElementsAfterModalClose);
    }
  };

  _handleModalCancel = () => {
    const {
      cancelModal: { openFormulaCancelModal },
    } = this.props;

    if (openFormulaCancelModal) {
      this._toggleCancelModal();
    }

    this._cancelNavigation();
  };

  handleSaveFormula = async () => {
    const {
      closeFormulaPanelFlyout,
      saveFormula,
      updateFormula,
      formulaInProgress,
    } = this.props;
    if (formulaInProgress.isCreate()) {
      await saveFormula();
    } else {
      await updateFormula();
    }
    closeFormulaPanelFlyout();
  };

  render() {
    const {
      showFormulaPanel,
      cancelModal: { openFormulaCancelModal },
    } = this.props;
    const { showNavigationConfirm } = this.state;
    return (
      <>
        <Flyout
          className={`${FORMULA_PANEL_BLOCK}__flyout`}
          show={showFormulaPanel}
          isAnimated
        >
          {showFormulaPanel && (
            <>
              <FlyoutHeader enableBack onBack={this._determineActionOnCancel}>
                <FormulaPanelHeader />
              </FlyoutHeader>
              <FlyoutPanelContent>
                <FormulaPanelContentContainer
                  onCancel={this._determineActionOnCancel}
                  saveFormula={this.handleSaveFormula}
                />
              </FlyoutPanelContent>
            </>
          )}
        </Flyout>
        {(showNavigationConfirm || openFormulaCancelModal) && (
          <FormulaCancelModal
            onClickYes={this._confirmCancelFromCancelModal}
            onClickNo={this._handleModalCancel}
          />
        )}
      </>
    );
  }
}

FormulaPanel.propTypes = {
  /**A boolean indicating if we should show the formula panel or not. */
  showFormulaPanel: PropTypes.bool.isRequired,
  /**The action that is fired when we need to hide the formula panel. */
  closeFormulaPanelFlyout: PropTypes.func.isRequired,
  /** Current formula form in progress */
  formulaInProgress: PropTypes.instanceOf(FormulaForm).isRequired,
  /**The action that is fired when user presses save button on formula-panel */
  saveFormula: PropTypes.func.isRequired,
  /** Action for updating formula */
  updateFormula: PropTypes.func.isRequired,
  /** Action to open the note panel */
  toggleNotesPanel: PropTypes.func,
  /** object value containing the cancel modal model */
  cancelModal: PropTypes.object,
  /** action fired to open the internal reference cancel modal */
  toggleInternalReferenceCancelModal: PropTypes.func,
  /** action fired to indicate the note panel will be opened after modal is confirmed */
  openCancelModalAfterModalConfirm: PropTypes.func,
  /** action fired to store the selected note in redux */
  storeSelectedNoteId: PropTypes.func,
  /** action fired to store the psuedo id of the selected note in redux */
  storeSelectedNotePseudoId: PropTypes.func,
  /**The action to open the section. */
  onSectionClick: PropTypes.func,
  /**The action to open the formula cancel modal. */
  toggleFormulaCancelModal: PropTypes.func,
  /** Action to cancel copy formula */
  cancelCopyFormula: PropTypes.func,
  /** Action to select note from content panel */
  selectNoteFromContentPanel: PropTypes.func,
  /** returns true if statement is OCR statement */
  isOCR: PropTypes.bool,
  /** Navigate function provided by withRouter HOC */
  navigate: PropTypes.func.isRequired,
};

export default withRouter(FormulaPanel);
